import React, { useState, useMemo } from 'react'
import { PublicKey } from '@solana/web3.js'
import { useWallet } from '@solana/wallet-adapter-react'
import { 
  History, 
  CheckCircle2,
  XCircle,
  Clock,
  Copy,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Search,
  Calendar,
  AlertTriangle,
  MinusCircle,
  ArrowUpDown
} from 'lucide-react'
import BN from 'bn.js'

import { Proposal } from '../lib/anchorClient'
import { DisplayUtils } from '../lib/solana'
import { cn, NumberUtils, DateUtils, A11yUtils } from '../lib/utils'

/**
 * Historical proposal data with computed fields
 * Extends base proposal with display-friendly information
 */
interface HistoricalProposal {
  proposal: Proposal
  publicKey: PublicKey
  id: string
  requestedSol: number
  createdDate: Date
  endedDate: Date
  duration: number
  turnoutPercentage: number
  yesPercentage: number
  noPercentage: number
  abstainPercentage: number
  outcome: 'Passed' | 'Failed' | 'Expired' | 'Executed'
  outcomeReason: string
}

/**
 * Props for the ProposalHistory component
 * Defines the structure for historical proposal display
 */
interface ProposalHistoryProps {
  proposals: Array<{ account: Proposal; publicKey: PublicKey }>
  totalShares: BN
  loading?: boolean
  error?: string | null
  onRefresh?: () => void
  className?: string
}

/**
 * Sort configuration for proposal table
 */
interface SortConfig {
  key: keyof HistoricalProposal | null
  direction: 'asc' | 'desc'
}

/**
 * Filter options for proposals
 */
interface FilterConfig {
  outcome: 'all' | 'Passed' | 'Failed' | 'Expired' | 'Executed'
  timeRange: 'all' | '7d' | '30d' | '90d'
}

/**
 * ProposalHistory Component - Displays historical proposals with voting outcomes
 * Shows proposal details, voting statistics, turnout rates, and final outcomes
 * Includes sorting, filtering, search capabilities with accessibility features
 */
export function ProposalHistory({
  proposals,
  totalShares,
  loading = false,
  error = null,
  onRefresh: _onRefresh,
  className
}: ProposalHistoryProps): JSX.Element {
  const { publicKey: _connectedWallet } = useWallet()
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'createdDate', direction: 'desc' })
  const [filterConfig, setFilterConfig] = useState<FilterConfig>({ outcome: 'all', timeRange: 'all' })
  const [searchTerm, setSearchTerm] = useState('')
  const [_copySuccess, setCopySuccess] = useState<string | null>(null)
  const [expandedProposal, setExpandedProposal] = useState<string | null>(null)

  /**
   * Transform raw proposal data into display-friendly format
   * Calculates voting statistics and determines outcomes
   */
  const enrichedProposals = useMemo((): HistoricalProposal[] => {
    if (!proposals.length || totalShares.eq(new BN(0))) return []

    return proposals.map((item, _index) => {
      const proposal = item.account
      // V2: Use requestedLamports (renamed from requestedAmount)
      const requestedSol = proposal.requestedLamports.toNumber() / 1e9
      const createdDate = new Date(proposal.createdAtTs.toNumber() * 1000)
      const endedDate = new Date(proposal.deadlineTs.toNumber() * 1000)
      const duration = (endedDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60) // hours

      // Calculate voting statistics
      const totalVotes = proposal.yesWeight.add(proposal.noWeight).add(proposal.abstainWeight)
      const snapshotShares = proposal.totalSnapshotShares.toNumber()
      const turnoutPercentage = snapshotShares > 0 ? (totalVotes.toNumber() / snapshotShares) * 100 : 0
      
      const yesPercentage = snapshotShares > 0 ? (proposal.yesWeight.toNumber() / snapshotShares) * 100 : 0
      const noPercentage = snapshotShares > 0 ? (proposal.noWeight.toNumber() / snapshotShares) * 100 : 0
      const abstainPercentage = snapshotShares > 0 ? (proposal.abstainWeight.toNumber() / snapshotShares) * 100 : 0

      // Determine outcome and reason
      let outcome: HistoricalProposal['outcome'] = 'Failed'
      let outcomeReason = ''

      if ('executed' in proposal.status) {
        outcome = 'Executed'
        outcomeReason = `Passed with ${turnoutPercentage.toFixed(1)}% turnout and executed successfully`
      } else if ('failed' in proposal.status) {
        if (turnoutPercentage < 30) {
          outcome = 'Expired'
          outcomeReason = `Expired due to insufficient turnout (${turnoutPercentage.toFixed(1)}% < 30%)`
        } else {
          outcome = 'Failed'
          outcomeReason = `Failed - No votes (${noPercentage.toFixed(1)}%) exceeded Yes votes (${yesPercentage.toFixed(1)}%)`
        }
      } else if ('active' in proposal.status) {
        // This shouldn't happen in historical data, but handle gracefully
        if (Date.now() > proposal.deadlineTs.toNumber() * 1000) {
          if (turnoutPercentage >= 30 && yesPercentage > noPercentage) {
            outcome = 'Passed'
            outcomeReason = `Passed with ${yesPercentage.toFixed(1)}% Yes votes`
          } else if (turnoutPercentage < 30) {
            outcome = 'Expired'
            outcomeReason = `Expired due to insufficient turnout`
          } else {
            outcome = 'Failed'
            outcomeReason = `Failed with ${noPercentage.toFixed(1)}% No votes`
          }
        }
      }

      return {
        proposal,
        publicKey: item.publicKey,
        id: item.publicKey.toBase58(),
        requestedSol,
        createdDate,
        endedDate,
        duration,
        turnoutPercentage,
        yesPercentage,
        noPercentage,
        abstainPercentage,
        outcome,
        outcomeReason
      }
    })
  }, [proposals, totalShares])

  /**
   * Apply filtering based on user selections
   * Filters by outcome type and time range
   */
  const filteredProposals = useMemo(() => {
    let filtered = enrichedProposals

    // Filter by outcome
    if (filterConfig.outcome !== 'all') {
      filtered = filtered.filter(p => p.outcome === filterConfig.outcome)
    }

    // Filter by time range
    if (filterConfig.timeRange !== 'all') {
      const now = new Date()
      const cutoffDays = filterConfig.timeRange === '7d' ? 7 : 
                        filterConfig.timeRange === '30d' ? 30 : 90
      const cutoffDate = new Date(now.getTime() - cutoffDays * 24 * 60 * 60 * 1000)
      
      filtered = filtered.filter(p => p.createdDate >= cutoffDate)
    }

    // Filter by search term (destination address)
    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.proposal.destinationWallet.toBase58().toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    return filtered
  }, [enrichedProposals, filterConfig, searchTerm])

  /**
   * Apply sorting based on current sort configuration
   * Handles different data types appropriately
   */
  const sortedProposals = useMemo(() => {
    if (!sortConfig.key) return filteredProposals

    return [...filteredProposals].sort((a, b) => {
      const aValue = a[sortConfig.key!]
      const bValue = b[sortConfig.key!]

      let comparison = 0
      if (aValue instanceof Date && bValue instanceof Date) {
        comparison = aValue.getTime() - bValue.getTime()
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue
      } else {
        comparison = String(aValue).localeCompare(String(bValue))
      }

      return sortConfig.direction === 'desc' ? -comparison : comparison
    })
  }, [filteredProposals, sortConfig])

  /**
   * Handle column sorting with direction toggle
   * Updates sort configuration and provides user feedback
   */
  const handleSort = (key: keyof HistoricalProposal) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }))
  }

  /**
   * Handle copying addresses to clipboard with user feedback
   * Provides accessibility announcements
   */
  const handleCopyAddress = async (address: PublicKey, label: string) => {
    try {
      await navigator.clipboard.writeText(address.toBase58())
      setCopySuccess(label)
      A11yUtils.announceToScreenReader(`Copied ${label} to clipboard`)
      setTimeout(() => setCopySuccess(null), 2000)
    } catch (err) {
      console.error('Failed to copy address:', err)
    }
  }

  /**
   * Get status color for outcomes
   * Returns appropriate color class for each outcome type
   */
  const getOutcomeColor = (outcome: HistoricalProposal['outcome']): string => {
    switch (outcome) {
      case 'Passed':
      case 'Executed':
        return 'text-status-success'
      case 'Failed':
        return 'text-status-error'
      case 'Expired':
        return 'text-status-warning'
      default:
        return 'text-text-muted'
    }
  }

  /**
   * Get status icon for outcomes
   * Returns appropriate icon component for each outcome type
   */
  const getOutcomeIcon = (outcome: HistoricalProposal['outcome']) => {
    switch (outcome) {
      case 'Passed':
      case 'Executed':
        return <CheckCircle2 className="w-4 h-4" />
      case 'Failed':
        return <XCircle className="w-4 h-4" />
      case 'Expired':
        return <Clock className="w-4 h-4" />
      default:
        return <MinusCircle className="w-4 h-4" />
    }
  }

  /**
   * Render sort indicator for column headers
   * Shows current sort direction with accessibility labels
   */
  const SortIcon = ({ column }: { column: keyof HistoricalProposal }) => {
    if (sortConfig.key !== column) {
      return <ArrowUpDown className="w-4 h-4 text-text-muted" />
    }
    return sortConfig.direction === 'desc' ? (
      <ChevronDown className="w-4 h-4 text-cyber-cyan" aria-label="Sorted descending" />
    ) : (
      <ChevronUp className="w-4 h-4 text-cyber-cyan" aria-label="Sorted ascending" />
    )
  }

  const searchId = A11yUtils.generateId('proposal-search')
  const tableId = A11yUtils.generateId('proposal-history-table')

  // Error state
  if (error) {
    return (
      <div className={cn("cyber-card p-6", className)}>
        <div className="flex items-center space-x-3 text-status-error">
          <AlertTriangle className="w-6 h-6" />
          <div className="flex-1">
            <h3 className="font-header text-lg">Error Loading Proposal History</h3>
            <p className="text-sm text-text-secondary">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  // Loading state
  if (loading) {
    return (
      <div className={cn("cyber-card p-6", className)}>
        <div className="animate-pulse space-y-4">
          <div className="flex justify-between items-center">
            <div className="h-6 bg-dark-surface rounded w-1/4"></div>
            <div className="h-8 bg-dark-surface rounded w-1/3"></div>
          </div>
          <div className="h-px bg-dark-border"></div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex justify-between items-center py-4">
              <div className="h-4 bg-dark-surface rounded w-1/4"></div>
              <div className="h-4 bg-dark-surface rounded w-1/6"></div>
              <div className="h-4 bg-dark-surface rounded w-1/6"></div>
              <div className="h-4 bg-dark-surface rounded w-1/8"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Empty state
  if (!enrichedProposals.length) {
    return (
      <div className={cn("cyber-card p-6", className)}>
        <div className="text-center">
          <History className="w-12 h-12 mx-auto mb-4 text-text-muted" />
          <h3 className="font-header text-lg text-cyber-cyan mb-2">No Proposal History</h3>
          <p className="text-text-secondary">
            This pool has no completed proposals yet. Historical data will appear here after proposals are voted on and finalized.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className={cn("cyber-card p-6", className)}>
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 space-y-4 lg:space-y-0">
        <div>
          <h2 className="font-header text-2xl text-cyber-cyan text-glow mb-2">
            Proposal History
          </h2>
          <p className="text-text-secondary">
            {enrichedProposals.length} total proposal{enrichedProposals.length !== 1 ? 's' : ''} • 
            {' '}{sortedProposals.length} shown after filters
          </p>
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
          {/* Search */}
          <div className="relative">
            <label htmlFor={searchId} className="sr-only">
              Search by destination address
            </label>
            <input
              id={searchId}
              type="text"
              placeholder="Search destination address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="cyber-input pl-10 pr-4 py-2 w-full sm:w-64"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-muted" />
          </div>

          {/* Outcome Filter */}
          <select
            value={filterConfig.outcome}
            onChange={(e) => setFilterConfig(prev => ({ ...prev, outcome: e.target.value as any }))}
            className="cyber-input px-3 py-2"
            aria-label="Filter by outcome"
          >
            <option value="all">All Outcomes</option>
            <option value="Executed">Executed</option>
            <option value="Passed">Passed</option>
            <option value="Failed">Failed</option>
            <option value="Expired">Expired</option>
          </select>

          {/* Time Range Filter */}
          <select
            value={filterConfig.timeRange}
            onChange={(e) => setFilterConfig(prev => ({ ...prev, timeRange: e.target.value as any }))}
            className="cyber-input px-3 py-2"
            aria-label="Filter by time range"
          >
            <option value="all">All Time</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto -mx-6">
        <div className="inline-block min-w-full px-6">
          <table 
            id={tableId}
            className="w-full cyber-table"
            role="table"
            aria-label="Proposal history table"
          >
            <thead>
              <tr role="row">
                <th 
                  scope="col"
                  className="text-left cursor-pointer hover:bg-dark-card transition-colors"
                  onClick={() => handleSort('createdDate')}
                  onKeyDown={(e) => e.key === 'Enter' && handleSort('createdDate')}
                  tabIndex={0}
                  role="columnheader"
                >
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4" />
                    <span>Created</span>
                    <SortIcon column="createdDate" />
                  </div>
                </th>
                <th 
                  scope="col"
                  className="text-right cursor-pointer hover:bg-dark-card transition-colors"
                  onClick={() => handleSort('requestedSol')}
                  onKeyDown={(e) => e.key === 'Enter' && handleSort('requestedSol')}
                  tabIndex={0}
                  role="columnheader"
                >
                  <div className="flex items-center justify-end space-x-2">
                    <span>Amount</span>
                    <SortIcon column="requestedSol" />
                  </div>
                </th>
                <th 
                  scope="col"
                  className="text-center cursor-pointer hover:bg-dark-card transition-colors"
                  onClick={() => handleSort('turnoutPercentage')}
                  onKeyDown={(e) => e.key === 'Enter' && handleSort('turnoutPercentage')}
                  tabIndex={0}
                  role="columnheader"
                >
                  <div className="flex items-center justify-center space-x-2">
                    <span>Turnout</span>
                    <SortIcon column="turnoutPercentage" />
                  </div>
                </th>
                <th 
                  scope="col"
                  className="text-center cursor-pointer hover:bg-dark-card transition-colors"
                  onClick={() => handleSort('outcome')}
                  onKeyDown={(e) => e.key === 'Enter' && handleSort('outcome')}
                  tabIndex={0}
                  role="columnheader"
                >
                  <div className="flex items-center justify-center space-x-2">
                    <span>Outcome</span>
                    <SortIcon column="outcome" />
                  </div>
                </th>
                <th scope="col" className="text-center w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedProposals.map((proposal) => (
                <React.Fragment key={proposal.id}>
                  <tr 
                    role="row"
                    className={cn(
                      "group cursor-pointer transition-colors",
                      expandedProposal === proposal.id && "bg-dark-surface"
                    )}
                    onClick={() => setExpandedProposal(
                      expandedProposal === proposal.id ? null : proposal.id
                    )}
                  >
                    {/* Created Date */}
                    <td role="cell" className="font-mono text-sm">
                      <div>
                        <div>{DateUtils.formatDate(proposal.createdDate.getTime() / 1000, { 
                          month: 'short', 
                          day: 'numeric' 
                        })}</div>
                        <div className="text-text-muted text-xs">
                          {proposal.createdDate.toLocaleDateString('en-US', { year: 'numeric' })}
                        </div>
                      </div>
                    </td>

                    {/* Requested Amount */}
                    <td role="cell" className="text-right font-mono">
                      <span className="text-cyber-cyan">
                        ◎{NumberUtils.formatCurrency(proposal.requestedSol, '', 2)}
                      </span>
                    </td>

                    {/* Turnout */}
                    <td role="cell" className="text-center">
                      <div className="flex flex-col items-center">
                        <span className={cn(
                          "font-mono text-sm",
                          proposal.turnoutPercentage >= 30 ? "text-status-success" : "text-status-warning"
                        )}>
                          {NumberUtils.formatPercentage(proposal.turnoutPercentage)}
                        </span>
                        <div className="w-12 bg-dark-panel rounded-full h-1 mt-1">
                          <div
                            className={cn(
                              "h-1 rounded-full transition-all duration-300",
                              proposal.turnoutPercentage >= 30 ? "bg-status-success" : "bg-status-warning"
                            )}
                            style={{ width: `${Math.min(proposal.turnoutPercentage, 100)}%` }}
                          />
                        </div>
                      </div>
                    </td>

                    {/* Outcome */}
                    <td role="cell" className="text-center">
                      <div className={cn("flex items-center justify-center space-x-2", getOutcomeColor(proposal.outcome))}>
                        {getOutcomeIcon(proposal.outcome)}
                        <span className="font-medium">{proposal.outcome}</span>
                      </div>
                    </td>

                    {/* Actions */}
                    <td role="cell" className="text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setExpandedProposal(expandedProposal === proposal.id ? null : proposal.id)
                        }}
                        className="cyber-button p-1.5"
                        aria-label={`${expandedProposal === proposal.id ? 'Collapse' : 'Expand'} proposal details`}
                      >
                        {expandedProposal === proposal.id ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                  </tr>

                  {/* Expanded Details */}
                  {expandedProposal === proposal.id && (
                    <tr role="row" className="bg-dark-surface">
                      <td colSpan={5} className="p-6">
                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Proposal Details */}
                          <div className="space-y-4">
                            <h4 className="font-header text-cyber-cyan mb-3">Proposal Details</h4>
                            
                            <div>
                              <p className="text-text-muted text-sm">Destination Wallet</p>
                              <div className="flex items-center space-x-2">
                                <code className="font-mono text-sm text-text-primary break-all">
                                  {DisplayUtils.formatPublicKey(proposal.proposal.destinationWallet)}
                                </code>
                                <button
                                  onClick={() => handleCopyAddress(
                                    proposal.proposal.destinationWallet, 
                                    `Proposal ${proposal.id.slice(0, 8)}`
                                  )}
                                  className="cyber-button p-1"
                                  aria-label="Copy destination address"
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => window.open(
                                    `https://solscan.io/account/${proposal.proposal.destinationWallet.toBase58()}`, 
                                    '_blank'
                                  )}
                                  className="cyber-button p-1"
                                  aria-label="View destination on Solscan"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                </button>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-text-muted text-sm">Voting Period</p>
                                <p className="font-mono text-sm">
                                  {proposal.duration.toFixed(1)} hours
                                </p>
                              </div>
                              <div>
                                <p className="text-text-muted text-sm">Ended</p>
                                <p className="font-mono text-sm">
                                  {DateUtils.formatDate(proposal.endedDate.getTime() / 1000)}
                                </p>
                              </div>
                            </div>

                            <div>
                              <p className="text-text-muted text-sm">Outcome Reason</p>
                              <p className="text-sm">{proposal.outcomeReason}</p>
                            </div>
                          </div>

                          {/* Voting Results */}
                          <div className="space-y-4">
                            <h4 className="font-header text-cyber-cyan mb-3">Voting Results</h4>
                            
                            <div className="space-y-3">
                              {/* Yes Votes */}
                              <div className="flex justify-between items-center">
                                <div className="flex items-center space-x-2">
                                  <CheckCircle2 className="w-4 h-4 text-status-success" />
                                  <span className="text-sm">Yes</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="font-mono text-status-success text-sm">
                                    {NumberUtils.formatPercentage(proposal.yesPercentage)}
                                  </span>
                                  <div className="w-16 bg-dark-panel rounded-full h-2">
                                    <div
                                      className="bg-status-success h-2 rounded-full"
                                      style={{ width: `${Math.min(proposal.yesPercentage, 100)}%` }}
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* No Votes */}
                              <div className="flex justify-between items-center">
                                <div className="flex items-center space-x-2">
                                  <XCircle className="w-4 h-4 text-status-error" />
                                  <span className="text-sm">No</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="font-mono text-status-error text-sm">
                                    {NumberUtils.formatPercentage(proposal.noPercentage)}
                                  </span>
                                  <div className="w-16 bg-dark-panel rounded-full h-2">
                                    <div
                                      className="bg-status-error h-2 rounded-full"
                                      style={{ width: `${Math.min(proposal.noPercentage, 100)}%` }}
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Abstain Votes */}
                              <div className="flex justify-between items-center">
                                <div className="flex items-center space-x-2">
                                  <MinusCircle className="w-4 h-4 text-text-muted" />
                                  <span className="text-sm">Abstain</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="font-mono text-text-muted text-sm">
                                    {NumberUtils.formatPercentage(proposal.abstainPercentage)}
                                  </span>
                                  <div className="w-16 bg-dark-panel rounded-full h-2">
                                    <div
                                      className="bg-text-muted h-2 rounded-full"
                                      style={{ width: `${Math.min(proposal.abstainPercentage, 100)}%` }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* No Results */}
      {filteredProposals.length === 0 && searchTerm && (
        <div className="text-center py-8">
          <Search className="w-8 h-8 mx-auto mb-3 text-text-muted" />
          <h3 className="font-header text-lg text-cyber-cyan mb-2">No Results Found</h3>
          <p className="text-text-secondary">
            No proposals found matching your search and filters.
          </p>
          <button
            onClick={() => {
              setSearchTerm('')
              setFilterConfig({ outcome: 'all', timeRange: 'all' })
            }}
            className="cyber-button-primary mt-4"
          >
            Clear Filters
          </button>
        </div>
      )}
    </div>
  )
}

export default ProposalHistory